import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { HaService } from 'src/app/core/services/ha.service';

interface DialogData {
  currentHostIp?: string;
  otherHostIp?: string;
  error?: string;
}

@Component({
  selector: 'app-update-host-ip',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>Update Host IPs</h2>
      <button  class="close-button" (click)="onCancel()" type="button">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    
    <form [formGroup]="form" (ngSubmit)="onSubmit()">
      <mat-dialog-content class="dialog-content text-grey">
        <div class="input-group mt-3">
          <mat-form-field appearance="outline">
            <mat-label>Current SyncHPC Host IP</mat-label>
            <input matInput formControlName="currentHostIp" placeholder="e.g., 192.168.1.10" />
            <mat-error>{{ getErrorMessage('currentHostIp') }}</mat-error>
          </mat-form-field>
        </div>

        <div class="input-group">
          <mat-form-field appearance="outline">
            <mat-label>Other SyncHPC Host IP</mat-label>
            <input matInput formControlName="otherHostIp" placeholder="e.g., 192.168.1.11" />
            <mat-error>{{ getErrorMessage('otherHostIp') }}</mat-error>
          </mat-form-field>
        </div>

        <div *ngIf="errorMessage" class="error-message">
          <span style="font-size: 10px;">{{ errorMessage }}</span>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="dialog-actions">
        <button mat-stroked-button type="button" (click)="onCancel()" class="cancel-btn">
          Cancel
        </button>
        <button 
          mat-flat-button
          type="submit" 
          class="update-btn btn-primary"
          [disabled]="form.invalid || isSubmitting">
          <mat-icon *ngIf="isSubmitting">sync</mat-icon>
          {{ isSubmitting ? 'Updating...' : 'Update' }}
        </button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [`
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 24px 16px 24px;
      border-bottom: 1px solid #e0e0e0;
      margin-bottom: 24px;
    }
    
    .dialog-header h2 {
      margin: 0;
      font-weight: 500;
    }
    
    .close-button {
      color: #666;
      border: none;
      background: none;
      cursor: pointer;
      padding: 0;
      font-size: 24px;
      transition: color 0.3s ease;
    }
    
    .dialog-content {
      min-width: 450px;
      max-width: 500px;
      padding: 0 24px;
    }
    
    .input-group {
      margin-bottom: 24px;
    }
    
    mat-form-field {
      width: 100%;
    }
    
    .mat-mdc-form-field {
      --mdc-filled-text-field-container-shape: 8px;
      --mdc-outlined-text-field-container-shape: 8px;
    }
    
    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #f44336;
      font-size: 14px;
      margin-top: 16px;
      padding: 12px 16px;
      background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%);
      border-radius: 8px;
      border-left: 4px solid #f44336;
      box-shadow: 0 2px 4px rgba(244, 67, 54, 0.1);
    }
    
    .dialog-actions {
      padding: 24px;
      gap: 12px;
      border-top: 1px solid #e0e0e0;
      margin-top: 24px;
      justify-content: flex-end;
    }
    
    .cancel-btn {
      min-width: 80px;
      height: 40px;
      border-radius: 8px;
      font-weight: 500;
      transition: all 0.3s ease;
    }
    
    .cancel-btn:hover {
      background-color: #f5f5f5;
    }
    
    .update-btn {
      min-width: 100px;
      height: 40px;
      border-radius: 8px;
      font-weight: 500;
      box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
      transition: all 0.3s ease;
    }
    
    .update-btn:hover:not([disabled]) {
      box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
      transform: translateY(-1px);
    }
    
    .update-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    .update-btn mat-icon {
      animation: spin 1s linear infinite;
      margin-right: 8px;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    /* Form field customization */
    ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      margin-top: 8px;
    }
    
    ::ng-deep .mat-mdc-form-field-hint-wrapper,
    ::ng-deep .mat-mdc-form-field-error-wrapper {
      padding: 0 16px;
    }
    
    /* Focus states */
    mat-form-field.mat-focused mat-icon[matSuffix] {
      color: #1976d2;
      opacity: 1;
    }
  `]
})
export class UpdateHostIpComponent {
  private readonly IP_PATTERN = /^(?:localhost(?::\d+)?|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$/i;

  form: FormGroup;
  isSubmitting = false;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private haService: HaService,
    private dialogRef: MatDialogRef<UpdateHostIpComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.form = this.fb.group({
      currentHostIp: [data?.currentHostIp || '', [Validators.required, Validators.pattern(this.IP_PATTERN)]],
      otherHostIp: [data?.otherHostIp || '', [Validators.required, Validators.pattern(this.IP_PATTERN)]]
    });
    this.errorMessage = data?.error;
  }

  getErrorMessage(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (control.errors['pattern']) return 'Please enter a valid IP address';
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels = {
      currentHostIp: 'Current IP',
      otherHostIp: 'Other IP'
    };
    return labels[fieldName as keyof typeof labels] || fieldName;
  }

  onSubmit(): void {
    if (this.form.invalid) return;

    this.isSubmitting = true;
    this.errorMessage = null;

    const payload = {
      CurrentHostIp: this.form.value.currentHostIp!,
      OtherHostIp: this.form.value.otherHostIp!
    };

    this.haService.updateHostIps(payload).subscribe({
      next: (response) => {
        this.dialogRef.close(response);
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Failed to update host IPs. Please try again.';
        this.isSubmitting = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}